import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Button, ClickAwayListener, Divider, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconEditPencil } from 'src/assets/icons/IconEditPencil';
import { LockedScoreButtons } from 'src/components/LockedScoreButtons';
import { LockedScorePopper } from 'src/components/LockedScorePopper';
import ScoreView from 'src/components/ScoreView';
import { ScoreWithDetailsLink } from 'src/components/ScoreWithDetailsLink';
import { TextAndWordCounter } from 'src/components/TextAndWordCounter';
import TextCounter from 'src/components/TextCounter';
import TooltipExclamation from 'src/components/TooltipExclamation';
import VariationEditableContent from 'src/components/VariationEditableContent';
import { VariationReadonlyContent } from 'src/components/VariationReadonlyContent';
import { ASSET_TYPE, BLOG_POST_ASSET_TYPE, LANDING_ASSET_TYPE } from 'src/data/asset';
import {
  BLOCKED_REASON,
  isLockedScoreBlockedReason,
  textIsTooLongForScoring,
} from 'src/data/common';
import { canCountCharactersForLanguage, isNonEnglish, LANGUAGE } from 'src/data/generation';
import { isImageGenerationTool } from 'src/data/generationTool';
import { isDocumentProjectType, PROJECT_TYPE } from 'src/data/project';
import { isVariationScorable, variationIsRecomposeOriginal } from 'src/data/variation';
import { useOnHover, useReferenceElement } from 'src/lib/hooks';
import { countWords } from 'src/lib/string';

import { ButtonText } from '../ButtonText';
import { CustomTypography, TYPOGRAPHY_VARIANT } from '../CustomTypography';
import { GenerationToolIcon } from '../GenerationToolIcon';
import { ImageBox } from '../ImageBox';
import { ImproveScoreBy } from '../ImproveScoreBy';

import { SelectGenerationTool } from './SelectGenerationTool';
import { useStyles } from './useStyles';

export const Variation = forwardRef(
  (
    {
      variationId,
      multiPromptVariationTitle,
      className,
      classes,
      projectType,
      assetType,
      description,
      mixtureSubAssetType,
      content,
      markup,
      score,
      counter,
      onChange,
      selected,
      onCloseError,
      error,
      leftActions,
      actions,
      forceDisplayActions,
      displayScore,
      forceDisplayHistogram,
      readOnly,
      updating,
      histogramData,
      contentSensitivity,
      loadingSensitivity,
      onScoreHover,
      detailsAllowed,
      onClick,
      forceClickAllow,
      blockedReason,
      onGetScoreClick,
      onLockedUpgradeClick,
      onUndoClick,
      undoDisabled,
      hideUndo,
      addons,
      hideContent,
      requireChangesConfirmation,
      editing,
      disabled,
      onSaveChanges,
      onCancelChanges,
      onClickAway,
      setEditor,
      actionsVisibleOnEdit,
      hideMarks,
      isOriginalCopy,
      improveReason,
      language,
      handleChangeClassifiedGenerationTool,
      generationTool,
      isLoadingClassifiedGenerationTool,
      scoringMethod,
      supportSize,
      leftOutsideHoverActions,
      rightOutsideHoverActions,
      forceHideErrorExclamation,
      hideActionsOnError,
      onCopy,
      onCut,
      onInsightsClick,
      isDraft,
      highlighted,
      originalVariationId,
      improvedVariationTitle,
      imageBlob,
      editAllowed,
      onDownloadImageClick,
      displayInsights,
      statusLabel,
      lastUpdateTime,
      ...restProps
    },
    ref
  ) => {
    const [transition, setTransition] = useState(false);
    const [selectedText, setSelectedText] = useState('');

    const refVariation = useRef();

    useEffect(() => {
      const onSelectionChange = () => {
        const rootElement = refVariation?.current;
        if (!rootElement) {
          return;
        }

        const selection = window.getSelection();
        if (selection.rangeCount === 0) {
          setSelectedText('');
          return;
        }

        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;

        // User might end up selecting nodes outside of the current Document (for example, some extension ShadowDom or iframe content)
        const isAncestorValid = commonAncestor?.getRootNode() === document;

        // If the current selection is in the "variation" element - update "selected text"
        if (isAncestorValid && rootElement.contains(commonAncestor)) {
          // filter out zwnbsp characters
          const selectedText = selection.toString()?.replace(/\uFEFF/g, '');
          setSelectedText(selectedText);
        } else {
          setSelectedText('');
        }
      };

      document.addEventListener('selectionchange', onSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', onSelectionChange);
      };
    }, [refVariation, setSelectedText]);

    useEffect(() => {
      if (highlighted) {
        setTransition(true);
      } else {
        setTimeout(() => {
          setTransition(false);
        }, 4000);
      }
    }, [highlighted]);

    const styles = useStyles({ classes, transition });

    const refActions = useRef();

    const counterValue = counter?.charsCount || 0;
    const counterStrict = counter?.strict || false;
    const hasActions = Boolean(actions);
    const actionsVisible = forceDisplayActions || (Boolean(error) && !hideActionsOnError);
    const isEmpty = counterValue === 0;

    const { hovered, handleMouseOver, handleMouseOut } = useOnHover();
    const hoveredOrSelected = hovered || selected;
    const isTooLong = content && typeof content === 'string' && textIsTooLongForScoring(content);

    const [editorFocused, setEditorFocused] = useState(false);
    const handleEditorFocusChange = useCallback((focused) => {
      setEditorFocused(focused);
    }, []);

    const handleErrorTooltipClose = useCallback(() => {
      if (onCloseError) {
        onCloseError();
      }
    }, [onCloseError]);

    const { referenceElement, setReferenceElement, clearReferenceElement } = useReferenceElement();

    const handleGetScoreClick = useCallback(
      (element) => {
        // Display internal popover for all blocked reasons except "performance predictions limit" - for this one we display "upgrade" popup in outside handler
        if (blockedReason !== BLOCKED_REASON.performancePredictionsLimit) {
          setReferenceElement(element);
        }

        if (onGetScoreClick) {
          onGetScoreClick();
        }
      },
      [blockedReason, onGetScoreClick, setReferenceElement]
    );

    const isRecomposeOriginal = variationIsRecomposeOriginal({
      generationTool,
      originalVariationId,
    });
    const isLimitedScoring = isLockedScoreBlockedReason(blockedReason);
    const isDisplayImprovedScoreBy =
      improveReason && isOriginalCopy && !editing && !originalVariationId;

    const displayActions = hasActions && (hoveredOrSelected || actionsVisible);
    const isEditorProject = isDocumentProjectType(projectType);
    const isImageTool = isImageGenerationTool(generationTool);
    const toolSelectorVisible =
      isVariationScorable(generationTool, lastUpdateTime) &&
      !isImageTool &&
      !isLoadingClassifiedGenerationTool;
    const handleOnClickAway = (event) => {
      if (referenceElement) return;

      (onClickAway || onCancelChanges)(event);
    };

    const generationToolIcon = generationTool && (
      <GenerationToolIcon
        className={styles.toolBadge}
        tool={generationTool}
      />
    );

    const descriptionText =
      improvedVariationTitle || multiPromptVariationTitle || description || mixtureSubAssetType;

    // make sure landing copy and all blog assets use chars counter and not word counter
    const displayCharCounterOnly =
      Object.values(BLOG_POST_ASSET_TYPE).includes(assetType) ||
      assetType === LANDING_ASSET_TYPE.landingCopy ||
      canCountCharactersForLanguage(language);

    const hideCounter = isImageTool;

    const counterValid = counterValue >= 0 && counterValue <= (counter?.maxChars || 0);
    const hasStatusLabel = statusLabel != null;

    return (
      <div
        className={cx(styles.root, className, {
          [styles.editing]: editing || editorFocused,
          [styles.empty]: isEmpty,
          [styles.selected]: hoveredOrSelected,
          [styles.displayEditorActions]: isEditorProject && displayActions,
          [styles.displayActions]: displayActions,
          [styles.actionsVisibleOnEdit]: actionsVisibleOnEdit,
          [styles.displayScore]: displayScore,
          [styles.detailsAllowed]: detailsAllowed,
          [styles.hasEditingControls]: requireChangesConfirmation,
          [styles.error]: error,
          [styles.extraHeight]: isLimitedScoring && !hasStatusLabel,
          [styles.withStatusHeight]: hasStatusLabel && !isLimitedScoring,
          [styles.withStatusHeightLocked]: hasStatusLabel && isLimitedScoring,
          [styles.isDraft]: isDraft,
          [styles.highlighted]: highlighted || isRecomposeOriginal,
          [styles.alignItemsStart]: isImageTool,
          [styles.counterInvalid]: counterStrict && !counterValid,
        })}
        ref={ref}
        onClick={onClick}
        onMouseOver={handleMouseOver}
        onMouseMove={handleMouseOver}
        onFocus={handleMouseOver}
        onMouseOut={handleMouseOut}
        onBlur={handleMouseOut}
        data-testid={`${generationTool}_${variationId}_VariationDraft`}
        {...restProps}
      >
        {leftOutsideHoverActions && hoveredOrSelected && (
          <div className={styles.leftOutsideHoverActions}>{leftOutsideHoverActions}</div>
        )}

        {rightOutsideHoverActions && hoveredOrSelected && (
          <div className={styles.rightOutsideHoverActions}>{rightOutsideHoverActions}</div>
        )}

        {leftActions && <div className={styles.leftActions}>{leftActions}</div>}

        <div
          className={styles.variationContent}
          onMouseOver={handleMouseOver}
          onMouseMove={handleMouseOver}
          ref={refVariation}
        >
          <div className={styles.descriptionWrapper}>
            {!editing && descriptionText ? (
              <Typography className={styles.description}>{descriptionText}</Typography>
            ) : null}
            {descriptionText && isRecomposeOriginal && !editing && <span>∙</span>}
            {!editing && isRecomposeOriginal && (
              <span className={styles.originalTag}>Original</span>
            )}
          </div>
          {isDisplayImprovedScoreBy && <ImproveScoreBy text={improveReason} />}
          {requireChangesConfirmation ? (
            editing ? (
              <ClickAwayListener
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
                onClickAway={handleOnClickAway}
              >
                <div className={styles.editorWrapper}>
                  <VariationEditableContent
                    className={cx(styles.editor, styles.editable)}
                    projectType={projectType}
                    assetType={assetType}
                    content={content}
                    onChange={onChange}
                    disabled={disabled}
                    addons={addons}
                    setEditor={setEditor}
                    onCopy={onCopy}
                    onCut={onCut}
                    hideMarks
                  />

                  <div className={styles.editingActions}>
                    <Button
                      onClick={onCancelChanges}
                      disabled={disabled}
                      variant="text"
                      color="primary"
                      size="small"
                    >
                      Cancel
                    </Button>

                    <Button
                      className={styles.editButton}
                      onClick={onSaveChanges}
                      disabled={disabled}
                      variant="contained"
                      color="primary"
                      size="small"
                      data-testid="applyVariationChangesButton"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </ClickAwayListener>
            ) : (
              <>
                <div
                  data-testid="variationDraftText"
                  className={styles.editorWrapper}
                >
                  {imageBlob && <ImageBox imageBlob={imageBlob} />}
                  <div>
                    {isImageTool && (
                      <CustomTypography
                        variant={TYPOGRAPHY_VARIANT.regular12}
                        className={styles.imageDescriptionTitle}
                      >
                        Description
                      </CustomTypography>
                    )}
                    <VariationReadonlyContent
                      className={isImageTool ? styles.editorSmall : styles.editor}
                      assetType={assetType}
                      content={content}
                      markup={markup}
                      hideMarks={hideMarks}
                    />
                  </div>

                  {editAllowed && hoveredOrSelected && (
                    <div className={styles.editWrapper}>
                      <IconEditPencil className={styles.editIcon} />
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            <div className={styles.editorWrapper}>
              <VariationEditableContent
                className={cx(styles.editor, { [styles.editable]: !readOnly })}
                projectType={projectType}
                assetType={assetType}
                content={content}
                onChange={onChange}
                onFocusChange={handleEditorFocusChange}
                disabled={readOnly}
                addons={addons}
                setEditor={setEditor}
                onCopy={onCopy}
                onCut={onCut}
              />
            </div>
          )}
        </div>

        {!hideCounter && !displayCharCounterOnly && (
          <TextAndWordCounter
            className={styles.textCounter}
            charsCount={selectedText.length || counterValue}
            maxChars={counter?.maxChars}
            wordsCount={selectedText ? countWords(selectedText) : counter?.wordsCount}
            maxWords={counter?.maxWords}
            strict={counterStrict && !selectedText}
            displayMaxChars={counterStrict && !selectedText}
          />
        )}

        {!hideCounter && displayCharCounterOnly && (
          <TextCounter
            className={styles.textCounter}
            count={selectedText.length || counterValue}
            maxCount={counter?.maxChars}
            strict={counterStrict}
          />
        )}

        <div
          className={cx(styles.actions, { [styles.fullWidthActions]: isEditorProject })}
          ref={refActions}
        >
          {actions}
        </div>
        {displayScore &&
          (isLimitedScoring ? (
            <LockedScoreButtons
              className={styles.lockedButtons}
              onGetScoreClick={handleGetScoreClick}
              onUndoClick={onUndoClick}
              disabled={undoDisabled}
              hideUndo={hideUndo || blockedReason === BLOCKED_REASON.performancePredictionsLimit}
              generationToolIcon={generationToolIcon}
              vertical
            />
          ) : detailsAllowed ? (
            <ScoreWithDetailsLink
              className={styles.score}
              score={counterStrict && !counterValid ? 0 : score}
              loading={updating}
              expanded={hoveredOrSelected || forceDisplayActions}
              blockedReason={blockedReason}
              locked={!!blockedReason}
              displayInsights={displayInsights}
              generationTool={generationTool}
              scoringMethod={scoringMethod}
              supportSize={supportSize}
              mixtureSubAssetType={mixtureSubAssetType}
              isNonEnglishLanguage={isNonEnglish(language)}
              onClick={
                (detailsAllowed && !isLimitedScoring && displayInsights) || forceClickAllow
                  ? onInsightsClick
                  : null
              }
              tooltipOverride={
                counterStrict && !counterValid
                  ? {
                      title: '',
                      text: 'This text exceeds\nthe character limit\nfor this asset type.',
                    }
                  : null
              }
            />
          ) : (
            <ScoreView
              className={styles.score}
              histogramData={histogramData}
              contentSensitivity={contentSensitivity}
              loadingSensitivity={loadingSensitivity}
              score={score}
              updating={updating}
              onHover={onScoreHover}
              forceDisplayHistogram={forceDisplayHistogram}
              blockedReason={blockedReason}
              locked={!!blockedReason}
              hideContent={hideContent}
              generationTool={generationTool}
            />
          ))}

        {hasStatusLabel && statusLabel}

        {toolSelectorVisible && (
          <div
            className={cx(styles.noScoreActions, {
              [styles.expanded]: hoveredOrSelected || forceDisplayActions,
            })}
          >
            <SelectGenerationTool
              className={styles.selectGenerationTool}
              classes={{ button: styles.selectGenerationToolButton }}
              value={generationTool}
              onChange={handleChangeClassifiedGenerationTool}
              isTooLong={isTooLong}
              editing={editing}
            />
            <Divider className={styles.divider} />
            <ButtonText
              className={styles.insightsButton}
              onClick={
                (detailsAllowed && !isLimitedScoring && displayInsights) || forceClickAllow
                  ? onInsightsClick
                  : null
              }
            >
              Insights <span className={styles.arrow}>&rsaquo;</span>
            </ButtonText>
          </div>
        )}
        {isLoadingClassifiedGenerationTool && (
          <Skeleton
            className={styles.skeleton}
            variant="circle"
          />
        )}
        {!forceHideErrorExclamation && (
          <TooltipExclamation
            classes={{ root: styles.errorTooltip }}
            open={Boolean(error)}
            anchor={refActions.current}
            onClose={handleErrorTooltipClose}
            content={error}
            icon="error"
            placement="top"
          />
        )}

        <LockedScorePopper
          referenceElement={referenceElement}
          onUpgrade={onLockedUpgradeClick}
          onClose={clearReferenceElement}
        />
      </div>
    );
  }
);

Variation.propTypes = {
  variationId: PropTypes.string.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape(),
  projectType: PropTypes.oneOf(Object.values(PROJECT_TYPE)),
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)),
  description: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.shape({}))])
    .isRequired,
  markup: PropTypes.arrayOf(PropTypes.shape()),
  score: PropTypes.number,
  counter: PropTypes.shape({
    charsCount: PropTypes.number,
    wordsCount: PropTypes.number,
    maxChars: PropTypes.number,
    maxWords: PropTypes.number,
    hint: PropTypes.string,
    strict: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  onCloseError: PropTypes.func,
  onScoreHover: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  leftActions: PropTypes.node,
  actions: PropTypes.node,
  forceDisplayActions: PropTypes.bool,
  displayScore: PropTypes.bool,
  forceDisplayHistogram: PropTypes.bool,
  readOnly: PropTypes.bool,
  updating: PropTypes.bool,
  histogramData: PropTypes.shape({
    ages: PropTypes.arrayOf(PropTypes.shape()),
    gender: PropTypes.shape({
      male: PropTypes.number,
      female: PropTypes.number,
    }),
  }),
  contentSensitivity: PropTypes.shape(),
  loadingSensitivity: PropTypes.bool,
  detailsAllowed: PropTypes.bool,
  onClick: PropTypes.func,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
  onGetScoreClick: PropTypes.func,
  onLockedUpgradeClick: PropTypes.func,
  onUndoClick: PropTypes.func,
  undoDisabled: PropTypes.bool,
  hideUndo: PropTypes.bool,
  addons: PropTypes.arrayOf(PropTypes.elementType),
  hideContent: PropTypes.bool,
  requireChangesConfirmation: PropTypes.bool,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  onSaveChanges: PropTypes.func,
  onCancelChanges: PropTypes.func,
  onClickAway: PropTypes.func,
  setEditor: PropTypes.func,
  actionsVisibleOnEdit: PropTypes.bool,
  hideMarks: PropTypes.bool,
  isOriginalCopy: PropTypes.bool,
  improveReason: PropTypes.string,
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
  handleChangeClassifiedGenerationTool: PropTypes.func,
  generationTool: PropTypes.string,
  isLoadingClassifiedGenerationTool: PropTypes.bool,
  supportSize: PropTypes.number,
  scoringMethod: PropTypes.shape({}),
  mixtureSubAssetType: PropTypes.string,
  multiPromptVariationTitle: PropTypes.string,
  forceClickAllow: PropTypes.bool,
  leftOutsideHoverActions: PropTypes.node,
  rightOutsideHoverActions: PropTypes.node,
  forceHideErrorExclamation: PropTypes.bool,
  hideActionsOnError: PropTypes.bool,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
  onInsightsClick: PropTypes.func,
  isDraft: PropTypes.bool,
  highlighted: PropTypes.bool,
  originalVariationId: PropTypes.string,
  improvedVariationTitle: PropTypes.string,
  editAllowed: PropTypes.bool,
  imageBlob: PropTypes.string,
  onDownloadImageClick: PropTypes.func,
  displayInsights: PropTypes.bool,
  statusLabel: PropTypes.node,
  lastUpdateTime: PropTypes.string,
};

Variation.defaultProps = {
  className: null,
  classes: null,
  projectType: null,
  assetType: null,
  description: null,
  onScoreHover: null,
  markup: null,
  score: 0,
  counter: null,
  selected: false,
  onCloseError: null,
  error: null,
  leftActions: null,
  actions: null,
  forceDisplayActions: false,
  displayScore: false,
  forceDisplayHistogram: false,
  readOnly: false,
  updating: false,
  histogramData: null,
  contentSensitivity: null,
  loadingSensitivity: false,
  detailsAllowed: false,
  onClick: null,
  blockedReason: null,
  onGetScoreClick: null,
  onLockedUpgradeClick: null,
  onUndoClick: null,
  undoDisabled: false,
  hideUndo: false,
  addons: [],
  hideContent: false,
  requireChangesConfirmation: false,
  editing: false,
  disabled: false,
  onSaveChanges: null,
  onCancelChanges: null,
  onClickAway: null,
  setEditor: null,
  actionsVisibleOnEdit: false,
  hideMarks: false,
  isOriginalCopy: false,
  improveReason: null,
  language: null,
  handleChangeClassifiedGenerationTool: null,
  generationTool: null,
  isLoadingClassifiedGenerationTool: false,
  supportSize: null,
  scoringMethod: null,
  mixtureSubAssetType: null,
  multiPromptVariationTitle: null,
  forceClickAllow: false,
  leftOutsideHoverActions: null,
  rightOutsideHoverActions: null,
  forceHideErrorExclamation: false,
  hideActionsOnError: false,
  onCopy: null,
  onCut: null,
  onInsightsClick: null,
  isDraft: false,
  highlighted: false,
  originalVariationId: null,
  improvedVariationTitle: null,
  editAllowed: true,
  imageBlob: null,
  onDownloadImageClick: null,
  displayInsights: false,
  statusLabel: null,
  lastUpdateTime: null,
};
